<template>
    <el-dialog
        :visible.sync='dialogVisible'
        append-to-body
        top='3vh'
        width='94%'
        custom-class='cloud-fn-script-dialog'
        :show-close='false'
        @closed='onDialogClosed'
    >
        <span slot='title' class='dialog-title-row'>
            <span>{{ dialogTitle }}</span>
            <span class='dialog-title-actions'>
                <el-button size='mini' :disabled='loading' @click.stop='downloadScriptZip'>下载</el-button>
                <el-button size='mini' type='primary' :disabled='loading'
                           @click.stop='openPublishDialog'>发布</el-button>
                <el-button size='mini' @click.stop='dialogVisible = false'>关闭</el-button>
            </span>
        </span>
        
        <div class='editor-shell' v-loading='loading'>
            <div class='editor-left'>
                <div class='left-header'>
                    <span class='left-header-title'>FILES</span>
                    <span class='left-header-actions'>
                        <el-button type='text' size='mini' @click='quickCreateFile'>+ 文件</el-button>
                        <el-button type='text' size='mini' @click='quickCreateFolder'>+ 目录</el-button>
                    </span>
                </div>
                <el-tree
                    ref='fileTree'
                    :data='treeData'
                    :props='treeProps'
                    node-key='path'
                    highlight-current
                    default-expand-all
                    @node-click='onTreeNodeClick'
                    @node-contextmenu='onNodeContextMenu'
                >
                    <span slot-scope='{ data }' class='tree-node-label'>
                        <i :class="data.isDir ? 'el-icon-folder' : 'el-icon-document'" class='tree-node-icon'></i>
                        <span>{{ data.label }}</span>
                    </span>
                </el-tree>
            </div>
            
            <div class='editor-main'>
                <div class='editor-right'>
                    <div class='tab-bar'>
                        <div
                            v-for='path in openTabs'
                            :key='path'
                            class='tab-item'
                            :class='{ active: path === activeFilePath }'
                            @click='openFile(path)'
                        >
                            <span class='tab-label'>{{ tabName(path) }}</span>
                            <i class='tab-close el-icon-close' @click.stop='closeTab(path)'></i>
                        </div>
                    </div>
                    <div v-show='activeFilePath' ref='monacoHost' class='monaco-host'></div>
                    <div v-show='!activeFilePath' class='monaco-placeholder'>请选择一个文件开始编辑</div>
                </div>
                
                <div class='helper-panel'>
                    <el-tabs v-model='assistTab' stretch>
                        <el-tab-pane v-for='tab in assistTabs' :key='tab.tab' :label='tab.label' :name='tab.tab'>
                            <div class='helper-list'>
                                <div v-for='item in tab.list' :key='`${tab.tab}_${item.code}`' class='helper-item'>
                                    <div class='helper-item-head'>
                                        <code class='helper-code'>{{ item.code }}</code>
                                        <div class='helper-actions'>
                                            <el-tooltip effect='dark' placement='top'>
                                                <div slot='content'>
                                                    <div>{{ item.tip || '无描述' }}</div>
                                                    <div v-if='item.paramsInfo' style='margin-top: 4px'>参数:
                                                        {{ item.paramsInfo }}
                                                    </div>
                                                </div>
                                                <el-button type='text' size='mini'>提示</el-button>
                                            </el-tooltip>
                                            <el-button type='text' size='mini' @click='insertAssistItem(item)'>使用
                                            </el-button>
                                        </div>
                                    </div>
                                    <div v-if='item.isFunction' class='helper-params'>
                                        {{ formatInputParams(item.inputParams) }}
                                    </div>
                                </div>
                            </div>
                        </el-tab-pane>
                    </el-tabs>
                </div>
            </div>
        </div>
        
        <div
            v-if='contextMenu.visible'
            ref='contextMenu'
            class='context-menu'
            :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
        >
            <div class='context-item' @click.stop='contextCreateFile'>新建文件</div>
            <div class='context-item' @click.stop='contextCreateFolder'>新建目录</div>
            <div class='context-item danger' @click.stop='contextDelete'>删除</div>
        </div>
        
        <el-dialog
            title='发布脚本'
            :visible.sync='publishDialogVisible'
            append-to-body
            width='520px'
            :close-on-click-modal='false'
        >
            <el-form label-width='100px' size='small'>
                <el-form-item label='变更日志' required>
                    <el-input
                        v-model.trim='scriptChangeLog'
                        type='textarea'
                        :autosize='{ minRows: 4, maxRows: 10 }'
                        placeholder='请输入本次脚本变更说明'
                    />
                </el-form-item>
            </el-form>
            <div slot='footer' class='dialog-footer'>
                <el-button @click='publishDialogVisible = false'>取消</el-button>
                <el-button type='primary' :loading='submitting' @click='submitPublish'>确认发布</el-button>
            </div>
        </el-dialog>
    </el-dialog>
</template>

<script>
import { message } from '@/utils/message';
import { cloudFunctionApi } from '@/components/api/Other/cloudFunctionApi';
import JSZip from 'jszip';

const FUNCTION_TYPE = {
    PROJECT: 'PROJECT',
    PROJECT_COMMON: 'PROJECT_COMMON'
};

const ASSIST_TAB = {
    CONTEXT: 'context',
    SYS: 'sys',
    PROJECT: 'project'
};

const CONTEXT_HELP_LIST = [
    { code: '_context.requestId', tip: '当前请求唯一标识', isFunction: false },
    { code: '_context.userId', tip: '当前用户ID（可能为空）', isFunction: false },
    { code: '_context.token', tip: '当前请求Token', isFunction: false },
    { code: '_context.headers', tip: '当前请求头对象', isFunction: false },
    { code: '_context.projectName', tip: '当前项目名', isFunction: false },
    { code: '_context.functionName', tip: '当前函数名', isFunction: false },
    { code: '_context.testMode', tip: '是否测试环境', isFunction: false }
];

const SYS_HELP_LIST = [
    {
        code: 'sys.logger.debug',
        tip: '打印调试日志',
        isFunction: true,
        inputParams: { message: '' },
        paramsInfo: '任意参数'
    },
    {
        code: 'sys.logger.info',
        tip: '打印信息日志',
        isFunction: true,
        inputParams: { message: '' },
        paramsInfo: '任意参数'
    },
    {
        code: 'sys.logger.warn',
        tip: '打印警告日志',
        isFunction: true,
        inputParams: { message: '' },
        paramsInfo: '任意参数'
    },
    {
        code: 'sys.logger.error',
        tip: '打印错误日志',
        isFunction: true,
        inputParams: { message: '' },
        paramsInfo: '任意参数'
    },
    {
        code: 'sys.request',
        tip: '发送HTTP请求（支持服务发现），serviceName和domain互斥, ',
        isFunction: true,
        inputParams: {
            serviceName: '',
            domain: '',
            uri: '',
            method: 'GET',
            data: {},
            params: {},
            headers: {},
            timeout: 10
        },
        paramsInfo: 'RequestConfig'
    },
    {
        code: 'sys.feign.cloudStorage.createObject',
        tip: '创建云存储对象',
        isFunction: true,
        inputParams: {
            appId: '',
            appKey: '',
            className: '',
            body: {}
        }
    },
    {
        code: 'sys.feign.cloudStorage.getObject',
        tip: '获取云存储对象',
        isFunction: true,
        inputParams: {
            appId: '',
            appKey: '',
            className: '',
            objectId: ''
        }
    },
    {
        code: 'sys.feign.cloudStorage.updateObject',
        tip: '更新云存储对象',
        isFunction: true,
        inputParams: {
            appId: '',
            appKey: '',
            className: '',
            objectId: '',
            body: {}
        }
    },
    {
        code: 'sys.feign.cloudStorage.deleteObject',
        tip: '删除云存储对象',
        isFunction: true,
        inputParams: {
            appId: '',
            appKey: '',
            className: '',
            objectId: ''
        }
    },
    {
        code: 'sys.feign.cloudStorage.queryObjects',
        tip: '查询云存储对象列表',
        isFunction: true,
        inputParams: {
            appId: '',
            appKey: '',
            className: '',
            query: {
                order: '-createTime',
                skip: 0,
                limit: 10,
                where: {}
            }
        }
    },
    {
        code: 'sys.feign.cache.get',
        tip: '读取服务端缓存，适合公共、低频变更、高频读取的数据。返回 Promise<string | null>',
        isFunction: true,
        inputParams: 'key',
        insertText: 'sys.feign.cache.get(\'cache:key\')',
        paramsInfo: 'key: string'
    },
    {
        code: 'sys.feign.cache.set',
        tip: '写入服务端缓存，value必须是字符串，对象请先JSON.stringify。返回 Promise<boolean>',
        isFunction: true,
        inputParams: 'key, value, seconds?',
        insertText: 'sys.feign.cache.set(\'cache:key\', JSON.stringify(data), 300)',
        paramsInfo: 'key: string, value: string, seconds?: number'
    },
    {
        code: 'sys.feign.cache.delete',
        tip: '批量删除服务端缓存，支持传入多个key或pattern。返回 Promise<boolean>',
        isFunction: true,
        inputParams: 'keySets',
        insertText: 'sys.feign.cache.delete([\'cache:key\'])',
        paramsInfo: 'keySets: string[]'
    },
    {
        code: 'sys.feign.lock.lock',
        tip: '可重入分布式锁加锁，同一个key + clientId可以重入。返回 Promise<boolean>',
        isFunction: true,
        inputParams: 'key, clientId, timeoutSecond?',
        insertText: 'sys.feign.lock.lock(\'lock:key\', `${_context.requestId}:operation`, 30)',
        paramsInfo: 'key: string, clientId: string, timeoutSecond?: number'
    },
    {
        code: 'sys.feign.lock.unLock',
        tip: '释放分布式锁，key和clientId必须与加锁时一致，建议放在finally中执行。返回 Promise<boolean>',
        isFunction: true,
        inputParams: 'key, clientId',
        insertText: 'sys.feign.lock.unLock(\'lock:key\', `${_context.requestId}:operation`)',
        paramsInfo: 'key: string, clientId: string'
    }
];

const PROJECT_HELP_LIST = [
    {
        code: '_project.xxx',
        tip: '调用项目通用函数（仅业务函数可用）',
        isFunction: true,
        inputParams: {},
        paramsInfo: '项目通用函数通过 _project.<name>(...) 调用'
    }
];
const MONACO_CDN_BASE = 'https://cdn.jsdelivr.net/npm/monaco-editor@0.34.1/min/vs';
let monacoLoaderPromise = null;

function loadMonacoFromCdn() {
    if (window.monaco && window.monaco.editor) {
        return Promise.resolve(window.monaco);
    }
    if (monacoLoaderPromise) {
        return monacoLoaderPromise;
    }
    monacoLoaderPromise = new Promise((resolve, reject) => {
        const startLoad = () => {
            if (!window.require) {
                reject(new Error('monaco loader missing require'));
                return;
            }
            window.require.config({
                paths: { vs: MONACO_CDN_BASE }
            });
            window.require(['vs/editor/editor.main'], () => {
                if (window.monaco && window.monaco.editor) {
                    resolve(window.monaco);
                } else {
                    reject(new Error('monaco global not ready'));
                }
            }, reject);
        };
        
        if (window.require) {
            startLoad();
            return;
        }
        
        const script = document.createElement('script');
        script.src = `${MONACO_CDN_BASE}/loader.js`;
        script.async = true;
        script.onload = startLoad;
        script.onerror = () => reject(new Error('load monaco loader failed'));
        document.head.appendChild(script);
    });
    return monacoLoaderPromise;
}

function normalizePath(path) {
    return String(path || '')
        .replace(/\\/g, '/')
        .replace(/^\/+/, '')
        .replace(/\/+$/, '')
        .replace(/\/{2,}/g, '/');
}

function splitPath(path) {
    return normalizePath(path).split('/').filter(Boolean);
}

function parentPath(path) {
    const parts = splitPath(path);
    return parts.length <= 1 ? '' : parts.slice(0, parts.length - 1).join('/');
}

function pathDepth(path) {
    return splitPath(path).length;
}

function guessLanguage(path) {
    const ext = path.slice(path.lastIndexOf('.') + 1).toLowerCase();
    if (ext === 'ts') return 'typescript';
    if (ext === 'json') return 'json';
    if (ext === 'css' || ext === 'scss' || ext === 'less') return 'css';
    if (ext === 'html' || ext === 'htm') return 'html';
    if (ext === 'md') return 'markdown';
    return 'javascript';
}

function isJavaScriptFile(path) {
    const p = String(path || '').toLowerCase();
    return p.endsWith('.js') || p.endsWith('.mjs') || p.endsWith('.cjs');
}

function commonPrefix(paths) {
    if (!paths || !paths.length) return '';
    const normalized = paths.map((item) => normalizePath(item));
    let prefix = normalized[0];
    for (let i = 1; i < normalized.length; i++) {
        const current = normalized[i];
        let j = 0;
        while (j < prefix.length && j < current.length && prefix[j] === current[j]) {
            j++;
        }
        prefix = prefix.slice(0, j);
        if (!prefix) break;
    }
    const idx = prefix.lastIndexOf('/');
    return idx >= 0 ? prefix.slice(0, idx + 1) : '';
}

function mapFiles(pathList, contentMap) {
    const root = commonPrefix(pathList);
    const fileMap = {};
    const directorySet = new Set();
    pathList.forEach((rawPath) => {
        const absolute = normalizePath(rawPath);
        const relative = normalizePath(absolute.startsWith(root) ? absolute.slice(root.length) : absolute);
        if (!relative) return;
        let content = '';
        if (Object.prototype.hasOwnProperty.call(contentMap, rawPath)) {
            content = contentMap[rawPath];
        } else if (Object.prototype.hasOwnProperty.call(contentMap, absolute)) {
            content = contentMap[absolute];
        }
        fileMap[relative] = content == null ? '' : String(content);
        const parts = splitPath(relative);
        for (let i = 1; i < parts.length; i++) {
            directorySet.add(parts.slice(0, i).join('/'));
        }
    });
    return { fileMap, directorySet };
}

function buildTreeData(directorySet, fileMap) {
    const root = [];
    const nodeMap = new Map();
    const ensureNode = (path, isDir) => {
        if (nodeMap.has(path)) {
            const existing = nodeMap.get(path);
            if (isDir && !existing.isDir) {
                existing.isDir = true;
                existing.children = [];
            }
            return existing;
        }
        const parts = splitPath(path);
        const node = {
            label: parts[parts.length - 1] || '',
            path,
            isDir,
            children: isDir ? [] : null
        };
        nodeMap.set(path, node);
        const parent = parentPath(path);
        if (!parent) {
            root.push(node);
        } else {
            const parentNode = ensureNode(parent, true);
            parentNode.children.push(node);
        }
        return node;
    };
    
    Array.from(directorySet).sort((a, b) => a.localeCompare(b)).forEach((dir) => ensureNode(dir, true));
    Object.keys(fileMap).sort((a, b) => a.localeCompare(b)).forEach((file) => ensureNode(file, false));
    
    const sortNodes = (list) => {
        list.sort((a, b) => {
            if (a.isDir !== b.isDir) return a.isDir ? -1 : 1;
            return a.label.localeCompare(b.label);
        });
        list.forEach((item) => {
            if (item.children && item.children.length) sortNodes(item.children);
        });
    };
    sortNodes(root);
    return root;
}

export default {
    name: 'CloudFunctionScriptDialog',
    props: {
        visible: { type: Boolean, default: false },
        versionRow: { type: Object, default: null },
        functionType: { type: String, default: FUNCTION_TYPE.PROJECT },
        functionName: { type: String, default: '' }
    },
    data() {
        return {
            loading: false,
            submitting: false,
            publishDialogVisible: false,
            scriptChangeLog: '',
            publishValidated: false,
            baseVersionId: null,
            fileMap: {},
            directorySet: new Set(),
            treeData: [],
            treeProps: { children: 'children', label: 'label' },
            assistTab: ASSIST_TAB.CONTEXT,
            activeFilePath: '',
            openTabs: [],
            monaco: null,
            editor: null,
            monacoConfigured: false,
            monacoExtraLibDisposable: null,
            modelMap: {},
            contextMenu: {
                visible: false,
                x: 0,
                y: 0,
                path: '',
                isDir: false
            }
        };
    },
    computed: {
        dialogVisible: {
            get() {
                return this.visible;
            },
            set(value) {
                this.$emit('update:visible', value);
            }
        },
        dialogTitle() {
            if (!this.versionRow) return '编辑脚本';
            return `编辑脚本 - v${this.versionRow.version} (#${this.versionRow.id})`;
        },
        assistTabs() {
            const tabs = [
                { tab: ASSIST_TAB.CONTEXT, label: '上下文', list: CONTEXT_HELP_LIST },
                { tab: ASSIST_TAB.SYS, label: '系统函数', list: SYS_HELP_LIST }
            ];
            if (this.functionType === FUNCTION_TYPE.PROJECT) {
                tabs.push({ tab: ASSIST_TAB.PROJECT, label: '项目函数', list: PROJECT_HELP_LIST });
            }
            return tabs;
        }
    },
    watch: {
        visible(value) {
            if (value && this.versionRow) {
                this.openLoad();
            }
        }
    },
    mounted() {
        document.addEventListener('click', this.hideContextMenu, true);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.hideContextMenu, true);
        this.disposeAll();
    },
    methods: {
        hideContextMenu(event) {
            if (!this.contextMenu.visible) return;
            const menu = this.$refs.contextMenu;
            if (menu && event && event.target && menu.contains(event.target)) {
                return;
            }
            this.contextMenu.visible = false;
        },
        formatInputParams(inputParams) {
            const params = inputParams == null ? {} : inputParams;
            if (typeof params === 'string') return params;
            try {
                return JSON.stringify(params, null, 2);
            } catch (e) {
                return '{}';
            }
        },
        getAssistInsertText(item) {
            if (!item) return '';
            if (item.insertText) return item.insertText;
            if (!item.isFunction) return item.code || '';
            const paramsText = this.formatInputParams(item.inputParams);
            return `${item.code || ''}(${paramsText})`;
        },
        async insertAssistItem(item) {
            if (!this.activeFilePath) {
                message.warning('请先打开一个文件');
                return;
            }
            if (!this.editor) {
                await this.openFile(this.activeFilePath);
            }
            if (!this.editor) return;
            const text = this.getAssistInsertText(item);
            if (!text) return;
            const selection = this.editor.getSelection();
            this.editor.executeEdits('cloud-fn-assist', [
                {
                    range: selection,
                    text,
                    forceMoveMarkers: true
                }
            ]);
            this.editor.focus();
        },
        tabName(path) {
            const parts = splitPath(path);
            return parts.length ? parts[parts.length - 1] : path;
        },
        resetData() {
            this.scriptChangeLog = '';
            this.baseVersionId = null;
            this.fileMap = {};
            this.directorySet = new Set();
            this.treeData = [];
            this.assistTab = ASSIST_TAB.CONTEXT;
            this.activeFilePath = '';
            this.openTabs = [];
            this.contextMenu = { visible: false, x: 0, y: 0, path: '', isDir: false };
        },
        disposeAll() {
            if (this.editor) {
                this.editor.dispose();
                this.editor = null;
            }
            if (this.monacoExtraLibDisposable) {
                this.monacoExtraLibDisposable.dispose();
                this.monacoExtraLibDisposable = null;
            }
            Object.keys(this.modelMap).forEach((key) => {
                if (this.modelMap[key]) this.modelMap[key].dispose();
            });
            this.modelMap = {};
            this.monacoConfigured = false;
        },
        onDialogClosed() {
            this.disposeAll();
            this.publishDialogVisible = false;
            this.publishValidated = false;
            this.resetData();
        },
        refreshTree() {
            this.treeData = buildTreeData(this.directorySet, this.fileMap);
        },
        buildDefaultIndex() {
            if (this.functionType === FUNCTION_TYPE.PROJECT_COMMON) {
                return '/**\n * project common function\n */\nexport function example(input) {\n  return input;\n}\n';
            }
            const valid = /^[A-Za-z][A-Za-z0-9_]*$/.test(this.functionName || '') ? this.functionName : 'main';
            return `export default async function ${valid}(input, context) {\n  return input;\n}\n`;
        },
        async openLoad() {
            this.resetData();
            this.disposeAll();
            this.loading = true;
            this.baseVersionId = this.versionRow.id;
            try {
                const listRes = await cloudFunctionApi.listScriptFiles({ id: this.versionRow.id });
                if (listRes.code !== 200) {
                    this.loading = false;
                    return;
                }
                const pathList = Array.isArray(listRes.data) ? listRes.data : (listRes.data && listRes.data.result) || [];
                if (!pathList.length) {
                    this.fileMap = { 'index.js': this.buildDefaultIndex() };
                    this.refreshTree();
                    this.loading = false;
                    await this.openFile('index.js');
                    return;
                }
                const readRes = await cloudFunctionApi.readFileContent(pathList);
                if (readRes.code !== 200) {
                    this.loading = false;
                    return;
                }
                const contentMap = readRes.data && typeof readRes.data === 'object' ? readRes.data : {};
                const mapped = mapFiles(pathList, contentMap);
                this.fileMap = mapped.fileMap;
                this.directorySet = mapped.directorySet;
                this.refreshTree();
                const firstFile = Object.keys(this.fileMap).sort((a, b) => a.localeCompare(b))[0];
                if (firstFile) await this.openFile(firstFile);
            } catch (e) {
                message.error('加载脚本失败');
            }
            this.loading = false;
        },
        async ensureMonaco() {
            if (!this.monaco) {
                try {
                    this.monaco = await loadMonacoFromCdn();
                } catch (e) {
                    message.error('Monaco 加载失败，请检查网络环境');
                    throw e;
                }
            }
            if (!this.monaco) {
                message.error('Monaco 初始化失败');
                throw new Error('monaco init failed');
            }
            this.configureMonaco();
            return this.monaco;
        },
        configureMonaco() {
            if (!this.monaco || this.monacoConfigured) return;
            this.monacoConfigured = true;
            const ts = this.monaco.languages.typescript;
            if (!ts || !ts.javascriptDefaults) return;
            ts.javascriptDefaults.setEagerModelSync(true);
            ts.javascriptDefaults.setDiagnosticsOptions({
                noSemanticValidation: false,
                noSyntaxValidation: false
            });
            ts.javascriptDefaults.setCompilerOptions({
                target: ts.ScriptTarget.ES2020,
                allowJs: true,
                checkJs: true,
                allowNonTsExtensions: true
            });
            if (this.monacoExtraLibDisposable) {
                this.monacoExtraLibDisposable.dispose();
            }
            this.monacoExtraLibDisposable = ts.javascriptDefaults.addExtraLib(
                [
                    'declare const sys: any;',
                    'declare const _context: any;',
                    'declare const _project: any;'
                ].join('\n'),
                'ts:cloud-function-global.d.ts'
            );
        },
        ensureModel(path) {
            if (this.modelMap[path]) return this.modelMap[path];
            const language = guessLanguage(path);
            const uriPath = path.startsWith('/') ? path : `/${path}`;
            const uri = this.monaco.Uri.parse(`file://${uriPath}`);
            const model = this.monaco.editor.createModel(this.fileMap[path] || '', language, uri);
            model.onDidChangeContent(() => {
                this.fileMap[path] = model.getValue();
            });
            this.modelMap[path] = model;
            return model;
        },
        async openFile(path) {
            if (!path || !Object.prototype.hasOwnProperty.call(this.fileMap, path)) return;
            await this.$nextTick();
            await this.ensureMonaco();
            const host = this.$refs.monacoHost;
            if (!host) return;
            if (!this.editor) {
                this.editor = this.monaco.editor.create(host, {
                    theme: 'vs-dark',
                    fontSize: 13,
                    tabSize: 2,
                    minimap: { enabled: true },
                    automaticLayout: true,
                    scrollBeyondLastLine: false
                });
            }
            const model = this.ensureModel(path);
            this.editor.setModel(model);
            this.activeFilePath = path;
            if (!this.openTabs.includes(path)) {
                this.openTabs.push(path);
            }
            this.$nextTick(() => {
                const tree = this.$refs.fileTree;
                if (tree) tree.setCurrentKey(path);
            });
        },
        closeTab(path) {
            const index = this.openTabs.indexOf(path);
            if (index === -1) return;
            this.openTabs.splice(index, 1);
            if (this.activeFilePath !== path) return;
            const next = this.openTabs[index] || this.openTabs[index - 1];
            if (next) {
                this.openFile(next);
            } else {
                this.activeFilePath = '';
                if (this.editor) this.editor.setModel(null);
            }
        },
        onTreeNodeClick(node) {
            if (node && !node.isDir) {
                this.openFile(node.path);
            }
        },
        onNodeContextMenu(event, data) {
            event.preventDefault();
            event.stopPropagation();
            this.contextMenu.visible = true;
            this.contextMenu.x = event.clientX;
            this.contextMenu.y = event.clientY;
            this.contextMenu.path = data.path;
            this.contextMenu.isDir = !!data.isDir;
        },
        quickCreateFile() {
            this.createFileAt('');
        },
        quickCreateFolder() {
            this.createFolderAt('');
        },
        contextCreateFile() {
            const base = this.contextMenu.isDir ? this.contextMenu.path : parentPath(this.contextMenu.path);
            this.createFileAt(base);
            this.hideContextMenu();
        },
        contextCreateFolder() {
            const base = this.contextMenu.isDir ? this.contextMenu.path : parentPath(this.contextMenu.path);
            this.createFolderAt(base);
            this.hideContextMenu();
        },
        contextDelete() {
            const path = this.contextMenu.path;
            if (!path) return;
            this.deletePath(path);
            this.hideContextMenu();
        },
        createFileAt(baseDir) {
            this.$prompt('相对路径，例如 index.js 或 lib/util.js', '新建文件', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                inputPattern: /^[^\s]+$/,
                inputErrorMessage: '路径不能为空且不能包含空格',
                inputValue: baseDir ? `${baseDir}/` : ''
            }).then(({ value }) => {
                const path = normalizePath(value);
                if (!path) return;
                if (Object.prototype.hasOwnProperty.call(this.fileMap, path)) {
                    message.warning('文件已存在');
                    return;
                }
                this.fileMap[path] = '';
                const parts = splitPath(path);
                for (let i = 1; i < parts.length; i++) {
                    this.directorySet.add(parts.slice(0, i).join('/'));
                }
                this.refreshTree();
                this.openFile(path);
            }).catch(() => {
            });
        },
        createFolderAt(baseDir) {
            this.$prompt('相对路径，例如 lib 或 lib/helper', '新建目录', {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                inputPattern: /^[^\s]+$/,
                inputErrorMessage: '路径不能为空且不能包含空格',
                inputValue: baseDir ? `${baseDir}/` : ''
            }).then(({ value }) => {
                const path = normalizePath(value);
                if (!path) return;
                this.directorySet.add(path);
                const parts = splitPath(path);
                for (let i = 1; i < parts.length; i++) {
                    this.directorySet.add(parts.slice(0, i).join('/'));
                }
                this.refreshTree();
            }).catch(() => {
            });
        },
        deletePath(path) {
            const fileExists = Object.prototype.hasOwnProperty.call(this.fileMap, path);
            if (fileExists) {
                delete this.fileMap[path];
                if (this.modelMap[path]) {
                    this.modelMap[path].dispose();
                    delete this.modelMap[path];
                }
                this.closeTab(path);
                this.refreshTree();
                return;
            }
            const prefix = `${path}/`;
            Object.keys(this.fileMap).forEach((file) => {
                if (file.startsWith(prefix)) {
                    delete this.fileMap[file];
                    if (this.modelMap[file]) {
                        this.modelMap[file].dispose();
                        delete this.modelMap[file];
                    }
                    this.closeTab(file);
                }
            });
            const nextDirSet = new Set();
            Array.from(this.directorySet).forEach((dir) => {
                if (!(dir === path || dir.startsWith(prefix))) {
                    nextDirSet.add(dir);
                }
            });
            this.directorySet = nextDirSet;
            this.refreshTree();
        },
        validateBeforePublish() {
            if (!Object.prototype.hasOwnProperty.call(this.fileMap, 'index.js')) {
                message.error('必须包含 index.js');
                return false;
            }
            if (this.functionType === FUNCTION_TYPE.PROJECT) {
                const content = String(this.fileMap['index.js'] || '');
                const name = String(this.functionName || '').trim();
                if (name) {
                    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const rule = new RegExp(`export\\s+default\\s+async\\s+function\\s+${escaped}\\s*\\(`);
                    if (!rule.test(content)) {
                        message.error(`index.js 必须包含 export default async function ${name}(`);
                        return false;
                    }
                }
            }
            return true;
        },
        async createScriptZipBlob() {
            const zip = new JSZip();
            Array.from(this.directorySet)
                .sort((a, b) => pathDepth(a) - pathDepth(b))
                .forEach((dir) => zip.folder(dir));
            Object.keys(this.fileMap).forEach((file) => {
                zip.file(file, String(this.fileMap[file] == null ? '' : this.fileMap[file]));
            });
            return zip.generateAsync({ type: 'blob' });
        },
        async downloadScriptZip() {
            if (!Object.keys(this.fileMap).length) {
                message.warning('当前没有可下载的脚本文件');
                return;
            }
            try {
                const blob = await this.createScriptZipBlob();
                const name = this.functionName || 'cloud-function';
                const version = this.versionRow && this.versionRow.version != null ? `-v${this.versionRow.version}` : '';
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${name}${version}.zip`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } catch (e) {
                message.error('下载失败');
            }
        },
        async validateEditorErrors() {
            try {
                await this.ensureMonaco();
                const jsPaths = Object.keys(this.fileMap).filter((path) => isJavaScriptFile(path));
                jsPaths.forEach((path) => {
                    this.ensureModel(path);
                });
                await new Promise((resolve) => setTimeout(resolve, 150));
                let firstError = null;
                jsPaths.forEach((path) => {
                    if (firstError) return;
                    const model = this.modelMap[path];
                    if (!model) return;
                    const markers = this.monaco.editor.getModelMarkers({ resource: model.uri }) || [];
                    const errorMarker = markers.find((item) => item.severity === this.monaco.MarkerSeverity.Error);
                    if (errorMarker) {
                        firstError = {
                            path,
                            message: errorMarker.message,
                            line: errorMarker.startLineNumber,
                            column: errorMarker.startColumn
                        };
                    }
                });
                if (firstError) {
                    return `[${firstError.path}] 第${firstError.line}行: ${firstError.message}`;
                }
                return null;
            } catch (e) {
                return null;
            }
        },
        async openPublishDialog() {
            const basePass = this.validateBeforePublish();
            if (!basePass) return;
            
            const errorMsg = await this.validateEditorErrors();
            if (errorMsg) {
                try {
                    await this.$confirm(
                        `编辑器检测到潜在的语法错误：\n${errorMsg}\n\n由于云函数环境特殊，部分报错可能是误报。是否忽略错误并继续发布？`,
                        '发布警告',
                        {
                            confirmButtonText: '忽略并继续',
                            cancelButtonText: '返回修改',
                            type: 'warning',
                            customClass: 'cloud-fn-confirm-br'
                        }
                    );
                } catch (e) {
                    return;
                }
            }
            
            this.publishValidated = true;
            this.publishDialogVisible = true;
        },
        async submitPublish() {
            if (!this.publishValidated) return;
            if (!this.scriptChangeLog) {
                message.error('请填写变更日志');
                return;
            }
            this.submitting = true;
            try {
                const blob = await this.createScriptZipBlob();
                const file = new File([blob], 'cloud-function-script.zip', { type: 'application/zip' });
                const res = await cloudFunctionApi.updateFunctionScript({
                    id: this.baseVersionId,
                    zipFile: file,
                    changeLog: this.scriptChangeLog
                });
                if (res.code === 200) {
                    message.success('发布成功，已创建新版本');
                    this.publishDialogVisible = false;
                    this.publishValidated = false;
                    this.dialogVisible = false;
                    this.$emit('success');
                }
            } catch (e) {
                message.error('发布失败');
            }
            this.submitting = false;
        }
    }
};
</script>

<style scoped>
.dialog-title-row {
    width: calc(100% - 20px);
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.dialog-title-actions {
    display: inline-flex;
    gap: 8px;
}

.editor-shell {
    display: flex;
    height: 74vh;
    min-height: 480px;
    border: 1px solid #2f2f2f;
    border-radius: 4px;
    overflow: hidden;
    background: #1e1e1e;
}

.editor-left {
    width: 300px;
    background: #252526;
    border-right: 1px solid #333;
    display: flex;
    flex-direction: column;
    overflow: auto;
}

.left-header {
    height: 36px;
    padding: 0 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #333;
    color: #c7c7c7;
}

.left-header-title {
    font-size: 12px;
    letter-spacing: 1px;
}

.tree-node-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
}

.tree-node-icon {
    color: #8fb3ff;
}

.editor-right {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.editor-main {
    flex: 1;
    display: flex;
    min-width: 0;
}

.helper-panel {
    width: 360px;
    border-left: 1px solid #333;
    background: #1f1f1f;
    overflow: hidden;
}

.helper-panel :deep(.el-tabs__header) {
    margin: 0;
    background: #252526;
    border-bottom: 1px solid #333;
}

.helper-panel :deep(.el-tabs__item) {
    color: #c8c8c8;
}

.helper-panel :deep(.el-tabs__item.is-active) {
    color: #fff;
}

.helper-panel :deep(.el-tabs__content) {
    height: calc(74vh - 40px);
    overflow: auto;
}

.helper-list {
    padding: 8px;
}

.helper-item {
    border: 1px solid #333;
    border-radius: 4px;
    padding: 8px;
    margin-bottom: 8px;
    background: #252526;
}

.helper-item-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
}

.helper-code {
    color: #dcdcaa;
    font-size: 12px;
    white-space: pre-wrap;
    word-break: break-all;
}

.helper-actions {
    display: inline-flex;
    gap: 4px;
    flex-shrink: 0;
}

.helper-params {
    margin-top: 8px;
    color: #9cdcfe;
    font-size: 12px;
    white-space: pre-wrap;
    word-break: break-word;
    background: #1e1e1e;
    border: 1px solid #2f2f2f;
    border-radius: 4px;
    padding: 8px;
}

.tab-bar {
    height: 36px;
    display: flex;
    align-items: stretch;
    background: #252526;
    border-bottom: 1px solid #333;
    overflow-x: auto;
}

.tab-item {
    min-width: 120px;
    max-width: 240px;
    padding: 0 10px;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    color: #bcbcbc;
    border-right: 1px solid #333;
    cursor: pointer;
}

.tab-item.active {
    background: #1e1e1e;
    color: #fff;
    border-top: 2px solid #007acc;
}

.tab-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.tab-close {
    font-size: 12px;
    color: #9a9a9a;
}

.tab-close:hover {
    color: #fff;
}

.monaco-host {
    flex: 1;
    min-height: 0;
}

.monaco-placeholder {
    flex: 1;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #888;
    font-size: 14px;
}

.context-menu {
    position: fixed;
    z-index: 3500;
    min-width: 140px;
    border: 1px solid #333;
    border-radius: 4px;
    background: #252526;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
    overflow: hidden;
}

.context-item {
    padding: 8px 12px;
    font-size: 13px;
    color: #d8d8d8;
    cursor: pointer;
}

.context-item:hover {
    background: #0e639c;
}

.context-item.danger:hover {
    background: #a1260d;
}
</style>

<style>
.cloud-fn-script-dialog .el-dialog__body {
    padding-top: 10px;
}

.cloud-fn-script-dialog .el-tree {
    background: transparent;
    color: #c8c8c8;
}

.cloud-fn-script-dialog .el-tree-node__content:hover {
    background-color: #2a2d2e;
}

.cloud-fn-script-dialog .el-tree--highlight-current .el-tree-node.is-current > .el-tree-node__content {
    background-color: #37373d;
    color: #fff;
}
</style>
